// 入门机器人：枚举手里能出的组合，按“不拆炸弹、小牌先走、不压搭档、对手快走完才炸”选牌。
// 只用自己看得见的信息（自己的手牌、桌面出牌、报牌公开的张数），不偷看别人手牌，也不看超过报牌线的张数。
// 难度分级与记牌由凌谋在具体游戏里扩展；这里是规则合法性的基线对手。
import { isJoker, isWild, power, BIG_JOKER, SMALL_JOKER } from './cards.ts';
import type { Card } from './cards.ts';
import { analyze, beats, bombTier, isBombType } from './rules.ts';
import type { Play } from './rules.ts';
import { partnerOf } from './match.ts';
import { unseenCounts } from './counter.ts';
import type { PlayerView } from './match.ts';

const natural = (pos: number): number => (pos === 1 ? 14 : pos);

/** 列出手牌能组成的候选出法（去重）。 */
export function candidates(hand: Card[], level: number): Play[] {
  const wilds = hand.filter((c) => isWild(c, level));
  const groups = new Map<number, Card[]>();
  for (const c of hand) if (!isWild(c, level) && !isJoker(c)) groups.set(c.rank, [...(groups.get(c.rank) ?? []), c]);
  const small = hand.filter((c) => c.rank === SMALL_JOKER);
  const big = hand.filter((c) => c.rank === BIG_JOKER);
  const sets: Card[][] = [];
  const push = (cs: Card[]) => { if (cs.length) sets.push(cs); };

  for (const g of groups.values()) {
    push(g.slice(0, 1));
    if (g.length >= 2) push(g.slice(0, 2));
    if (g.length >= 3) push(g.slice(0, 3));
    if (g.length >= 4) push(g);
    // 逢人配补张：凑对子、三张、炸弹（扣分在 choose 里，舍得用才用）
    for (let k = 1; k <= wilds.length; k++) {
      const w = wilds.slice(0, k);
      if (g.length + k === 2 || g.length + k === 3) push([...g.slice(0, 3 - k), ...w].slice(0, g.length + k));
      if (g.length + k >= 4) push([...g, ...w]);
    }
  }
  for (const j of [small, big]) { push(j.slice(0, 1)); if (j.length === 2) push(j); }
  if (wilds.length) push(wilds.slice(0, 1));
  if (small.length === 2 && big.length === 2) push([...small, ...big]);

  // 三带对：三张（可用逢人配补）+ 最小的对子（自然对或一对同色王）
  const pairs = [...groups.entries()].filter(([, g]) => g.length >= 2).sort((a, b) => a[0] - b[0]).map(([r, g]) => [r, g.slice(0, 2)] as const);
  const jokerPairs = [small, big].filter((j) => j.length === 2).map((j) => [j[0].rank, j] as const);
  for (const [r, g] of groups.entries()) {
    const need = Math.max(0, 3 - g.length);
    if (need > wilds.length) continue;
    const three = [...g.slice(0, 3), ...wilds.slice(0, need)];
    const p = [...pairs, ...jokerPairs].find(([pr]) => pr !== r);
    if (p) push([...three, ...p[1]]);
  }

  // 连牌：顺子、三连对、三同连张，必要时用逢人配补缺
  const runs: Array<[number, number]> = [[5, 1], [3, 2], [2, 3]];
  for (const [len, width] of runs) {
    for (let start = 1; start + len - 1 <= 14; start++) {
      const picked: Card[] = [];
      let missing = 0;
      for (let i = 0; i < len; i++) {
        const g = groups.get(natural(start + i)) ?? [];
        picked.push(...g.slice(0, width));
        missing += Math.max(0, width - g.length);
      }
      if (missing <= wilds.length) push([...picked, ...wilds.slice(0, missing)]);
    }
  }
  // 同花顺
  for (const suit of ['S', 'H', 'C', 'D'] as const) {
    for (let start = 1; start <= 10; start++) {
      const picked: Card[] = [];
      let missing = 0;
      for (let i = 0; i < 5; i++) {
        const c = hand.find((h) => h.suit === suit && h.rank === natural(start + i) && !isWild(h, level));
        if (c) picked.push(c); else missing++;
      }
      if (missing <= wilds.length) push([...picked, ...wilds.slice(0, missing)]);
    }
  }

  const seen = new Set<string>();
  const out: Play[] = [];
  for (const cs of sets) {
    for (const p of analyze(cs, level)) {
      const sig = `${p.type}:${p.key}:${cs.map((c) => c.id).sort((a, b) => a - b).join(',')}`;
      if (!seen.has(sig)) { seen.add(sig); out.push(p); }
    }
  }
  return out;
}

/** 这手牌会拆掉多少炸弹张（出非炸弹时用来扣分）。 */
function breaksBomb(p: Play, hand: Card[], level: number): number {
  if (isBombType(p.type)) return 0;
  const counts = new Map<number, number>();
  for (const c of hand) if (!isWild(c, level) && !isJoker(c)) counts.set(c.rank, (counts.get(c.rank) ?? 0) + 1);
  return p.cards.filter((c) => !isWild(c, level) && (counts.get(c.rank) ?? 0) >= 4).length;
}

const usesWild = (p: Play, level: number) => p.cards.filter((c) => isWild(c, level)).length;

/**
 * 难度：入门 = 基线打法；普通 = 记牌留大牌控场、搭档报牌两张以内时喂小单张；
 * 高手 = 普通 + 只有“控场牌”能接时先忍住、对手快走完时舍得拆炸弹去拦。只用公开信息。
 */
export type Difficulty = 'easy' | 'normal' | 'hard';
export const DIFFICULTY_NAME: Record<Difficulty, string> = { easy: '入门', normal: '普通', hard: '高手' };

/**
 * 估算这手牌还要几手出完（贪心拆牌）：炸弹各一手；四大天王一手；连牌（顺子、三连对、三同连张）各一手；
 * 三张优先带对子；其余单、对各一手。逢人配按单张计。普通及以上用它选“不让手数变多”的出法。
 */
export function movesNeeded(hand: Card[], level: number): number {
  const cnt = new Map<number, number>();
  let moves = 0;
  const jokers = hand.filter(isJoker);
  for (const c of hand) if (!isJoker(c) && !isWild(c, level)) cnt.set(c.rank, (cnt.get(c.rank) ?? 0) + 1);
  if (jokers.length === 4) moves++;
  else for (const r of [SMALL_JOKER, BIG_JOKER]) { const n = jokers.filter((j) => j.rank === r).length; moves += n === 2 ? 1 : n; }
  moves += hand.filter((c) => isWild(c, level)).length;
  for (const [r, n] of cnt) if (n >= 4) { moves++; cnt.delete(r); }
  const at = (pos: number) => cnt.get(pos === 1 ? 14 : pos) ?? 0;
  const take = (pos: number, k: number) => { const r = pos === 1 ? 14 : pos; cnt.set(r, at(pos) - k); };
  // 连牌：钢板（两个连续三张）、三连对、顺子（只用单张，不拆对子三张）
  for (const [len, width] of [[2, 3], [3, 2], [5, 1]] as const) {
    for (let start = 1; start + len - 1 <= 14; start++) {
      const run = Array.from({ length: len }, (_, i) => start + i);
      if (run.every((p) => at(p) === width)) { run.forEach((p) => take(p, width)); moves++; }
    }
  }
  let t = 0, pr = 0, sg = 0;
  for (const n of cnt.values()) { if (n === 3) t++; else if (n === 2) pr++; else if (n === 1) sg++; }
  return moves + t + Math.max(0, pr - t) + sg;
}

const minus = (hand: Card[], cards: Card[]) => { const ids = new Set(cards.map((c) => c.id)); return hand.filter((c) => !ids.has(c.id)); };

/** 这手牌是不是“控场牌”：没见过的牌里已经没有能凑出同张数、更大的同点牌（不算炸弹）。 */
function isBoss(p: Play, unseen: Map<number, number>, level: number): boolean {
  if (!['single', 'pair', 'triple'].includes(p.type)) return false;
  for (const [rank, n] of unseen) if (n >= p.size && power(rank, level) > p.key) return false;
  return true;
}

/** 打法性格：对手报牌到几张以内才炸、到几张以内才舍得用逢人配、自己剩几张以内就放开炸。 */
export interface BotStyle { bombAt: number; wildAt: number; ownBombAt: number }
export const STANDARD: BotStyle = { bombAt: 8, wildAt: 10, ownBombAt: 10 };

/**
 * 机器人决定出什么；返回空数组表示过。只拿 PlayerView（自己的手牌、桌面、报牌公开的张数），
 * 拿不到别人的手牌（掼蛋测试用例库 G2-1）。
 */
export function choose(v: PlayerView, style: BotStyle = STANDARD, difficulty: Difficulty = 'easy'): Card[] {
  const { hand, level, last } = v;
  const smart = difficulty !== 'easy';
  const unseen = smart ? unseenCounts(hand, v.played) : new Map<number, number>();
  const partner = partnerOf(v.seat);
  const partnerLeft = v.finished.includes(partner) ? null : v.counts[partner];
  const all = candidates(hand, level);
  const whole = all.find((p) => p.size === hand.length);

  if (!last) {
    if (whole) return whole.cards;
    const normal = all.filter((p) => !isBombType(p.type));
    const pool = normal.length ? normal : all;
    // 普通及以上：搭档报牌两张以内，喂最小的单张让他走
    if (smart && partnerLeft !== null && partnerLeft <= 2) {
      const feed = pool.filter((p) => p.type === 'single' && !breaksBomb(p, hand, level)).sort((a, b) => a.key - b.key)[0];
      if (feed) return feed.cards;
    }
    // 入门：小牌先走；同样小时多张的先走；拆炸弹、用逢人配都扣分
    // 普通及以上：先选出完后手数最少的出法（不拆顺子、连对、三带对），手数一样再走小牌，控场牌留到后面
    const cost = (p: Play) => p.key + breaksBomb(p, hand, level) * 20 + usesWild(p, level) * 8 - p.size * 0.6
      + (smart ? movesNeeded(minus(hand, p.cards), level) * 30 + (isBoss(p, unseen, level) ? 6 : 0) : 0);
    return pool.sort((a, b) => cost(a) - cost(b))[0].cards;
  }

  const t = last.play;
  const owner = last.seat;
  const ok = all.filter((p) => beats(p, t));
  if (whole && beats(whole, t)) return whole.cards;
  if (owner === partnerOf(v.seat)) return []; // 不压搭档
  const fits = ok.filter((p) => !isBombType(p.type) && breaksBomb(p, hand, level) === 0);
  // 先用不带逢人配的；只有逢人配能接时，对手报牌后才舍得用
  // 普通及以上：接牌也选出完后手数最少的，再选小的
  const keyOf = (p: Play) => (smart ? movesNeeded(minus(hand, p.cards), level) * 100 : 0) + p.key;
  const plain = fits.filter((p) => !usesWild(p, level)).sort((a, b) => keyOf(a) - keyOf(b));
  const ownerLeft = v.counts[owner]; // 报牌线以上为 null：只知道还多
  // 高手：接了反而让手数变多、对手又还早（张数没公开）时先不接，留着结构
  if (difficulty === 'hard' && plain.length && ownerLeft === null && movesNeeded(minus(hand, plain[0].cards), level) > movesNeeded(hand, level)) return [];
  if (plain.length) return plain[0].cards;
  // 高手：对手报牌 3 张以内，舍得拆炸弹去拦
  if (difficulty === 'hard' && ownerLeft !== null && ownerLeft <= 3) {
    const any = ok.filter((p) => !isBombType(p.type)).sort((a, b) => a.key - b.key)[0];
    if (any) return any.cards;
  }
  const threat = (ownerLeft !== null && ownerLeft <= style.bombAt) || hand.length <= style.ownBombAt;
  const wild = fits.filter((p) => usesWild(p, level)).sort((a, b) => usesWild(a, level) - usesWild(b, level) || a.key - b.key);
  if (wild.length && (threat || (ownerLeft !== null && ownerLeft <= style.wildAt))) return wild[0].cards;
  if (!threat) return [];
  const bombs = ok.filter((p) => isBombType(p.type)).sort((a, b) => bombTier(a) - bombTier(b) || a.key - b.key);
  return bombs[0]?.cards ?? [];
}
